import { Button, Grid, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CustomAccordion from '../CustomAccordion/CustomAccordion';
import TagsFilter from '../TagFilter/TagsFilter';
import BaseResponse from '../../app/BaseResponse';
import Utility from '../../app/utility';
import { showLoading } from '../LoadingView/reducers/LoadingViewReducer';
import SavedSearch, { ComponentType } from '../SavedSearch/SavedSearch';
import Swal from 'sweetalert2';
import SavedSearchApi, { SaveSavedSearchParam } from '../SavedSearch/api/SavedSearchApi';
import SavedSearchModel from '../SavedSearch/models/SavedSearchModel';
import { addSavedSearch, savedSearchSelector } from '../SavedSearch/reducers/SavedSearchReducer';
import { QueryItem } from '../Tonnage/models/TonnageQueryModel';
import AgentApi, { SearchAgentParam } from './api/AgentApi';
import AgentModel from './models/AgentModel';
import { AgentSearchSelector, resetAgentSearch, setAgentNames, setAgentSearchKeyword, setPortNames, setCountries, setSearchResultTotalRecord, setSelectedAgents, setTriggerAgentSearch, setTriggerFocusCompanyName, setCargoesHandling, setAgentSearchResults } from './reducers/AgentSearchReducer';
import { contactInputSelector } from '../Contact/reducers/ContactInputReducer';
import PortMultipleSelector from '../PortSelector/PortMultipleSelector';
import PortModel from '../PortSelector/models/PortModel';
import CargoTypeMultipleSelector from '../CargoTypeSelector/CargoTypeMultipleSelector';
import CargoTypeModel from '../CargoTypeSelector/models/CargoTypeModel';
import { cargoTypesToQueryItems, queryItemsToCargoTypes } from '../Cargo/CargoSearch';
import { AgentSelectorSelector, setAgentSelectorSearchResults } from '../AgentSelector/reducers/AgentSelectorReducer';

const AgentSearch: React.FunctionComponent = () => {
    const dispatch = useDispatch();
    const agentSearchData = useSelector(AgentSearchSelector);
    const savedSearchData = useSelector(savedSearchSelector);
    const contactInputViewData = useSelector(contactInputSelector);
    const AgentSelectorData = useSelector(AgentSelectorSelector);
    
    useEffect(() => {
        if (AgentSelectorData.searchResults.length > 0) {
            return;
        }

        AgentApi.GetAll()
            .then((response: BaseResponse<AgentModel[]>) => {
                if (response.success) {
                    dispatch(setAgentSelectorSearchResults(response.data))
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
    }, [])

    const portsToQueryItems = (ports: PortModel[]) => {
        return _.map(ports, (port) => {
            return {
                id: port.id.toString(),
                value: port.name,
                positive: port.positive
            } as QueryItem
        })
    }

    const queryItemsToPorts = (queryItems: QueryItem[]) => {
        return _.map(queryItems, (queryItem) => {
            return {
                id: parseInt(queryItem.id),
                name: queryItem.value,
                positive: queryItem.positive
            } as PortModel
        })
    };

    const saveSavedSearch = () => {
        Swal.fire({
            title: 'Provide Saved Search Name',
            input: 'text',
            inputAttributes: {
                autocapitalize: 'off'
            },
            showCancelButton: true,
            confirmButtonText: 'Sumbit',
            showLoaderOnConfirm: true,
            allowOutsideClick: false,
            confirmButtonColor: '#1976d2',
            preConfirm: (name) => {
                if (!name || name.length === 0) {
                    Swal.showValidationMessage("Name ")
                }
            },
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(showLoading(true));

                SavedSearchApi.Save({
                    name: result.value ? result.value : "",
                    component: ComponentType.Agent,
                    queryJson: JSON.stringify(agentSearchData.query)
                } as SaveSavedSearchParam)
                    .then((response: BaseResponse<SavedSearchModel>) => {
                        if (response.success) {
                            dispatch(addSavedSearch(response.data));
                            Utility.Alert.Success("Save Successful");
                        } else {
                            Utility.Alert.Error(response.message);
                        }
                    })
                    .catch((error) => {
                        Utility.Alert.Error(error);
                    })
                    .finally(() => {
                        dispatch(showLoading(false));
                    });
            }
        })
    }

    useEffect(() => {
        if (agentSearchData.triggerSearch === 0) {
            return;
        }

        dispatch(showLoading(true));

        AgentApi.Search({
            query: agentSearchData.query
        } as SearchAgentParam)
            .then((response: BaseResponse<AgentModel[]>) => {
                if (response.success) {
                    dispatch(setAgentSearchResults(response.data));
                    dispatch(setSearchResultTotalRecord(response.totalRecord));
                } else {
                    Utility.Alert.Error(response.message);
                }
            }).catch((error) => {
                Utility.Alert.Error(error);
            })
            .finally(() => {
                dispatch(showLoading(false));
            });
    }, [agentSearchData.triggerSearch])

    const getTonnageSearchClass = () => {
        let className = "nice-scroll ";
        if (savedSearchData.expanded) {
            className += "tonnage-search-wrapper";
        } else {
            className += "tonnage-search-wrapper-saved-search-hide";
        }

        return className;
    }

    return (
        <>
            <SavedSearch
                component={ComponentType.Agent}
            >
            </SavedSearch>
            <CustomAccordion
                title="Search"
                defaultOpen={true}
                contentClassName={getTonnageSearchClass()}>
                <div className='tonnage-search-body'>
                    <Grid container>
                        <Grid item xs={8}>
                            <Grid container>
                                <Grid xs={8}>
                                    <Tooltip title="Alt + W">
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            className="tonnage-search-save-button"
                                            onClick={() => {
                                                dispatch(setTriggerAgentSearch((new Date()).getTime()));
                                            }}
                                        >
                                            RUN SEARCH (w)
                                        </Button>
                                    </Tooltip>
                                </Grid>
                                <Grid xs={4}>
                                    <Tooltip title="Reset (Alt + Q)">
                                        <Button
                                            className="clear-input-button"
                                            color="success"
                                            variant="contained"
                                            onClick={() => {
                                                dispatch(resetAgentSearch());
                                                dispatch(setTriggerFocusCompanyName(new Date().getTime()));
                                            }}
                                        >
                                            <i className="fa fa-refresh" aria-hidden="true"></i>
                                        </Button>
                                    </Tooltip>
                                </Grid>
                            </Grid>

                        </Grid>
                        <Grid item xs={4}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => {
                                    saveSavedSearch();
                                }}
                            >
                                Save Search
                            </Button>
                        </Grid>
                    </Grid>
                    <TagsFilter
                        data={_.map(AgentSelectorData.searchResults, (item) => {
                            return {
                                id: item.id,
                                value: item.name,
                                positive: true
                            } as QueryItem
                        })}
                        useFreeText={true}
                        label="Agent Name"
                        onInputChange={(text: string) => {
                            dispatch(setAgentSearchKeyword(text));
                        }}
                        className="tonnage-search-item"
                        value={agentSearchData.query.agentNames}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setAgentNames(value));
                        }}
                        triggerFocusInput={agentSearchData.triggerFocusCompanyName}
                    ></TagsFilter>
                    <TagsFilter
                        data={_.map(contactInputViewData.staticResources.countries, (item) => {
                            return {
                                id: item.code,
                                value: item.code,
                                positive: true
                            } as QueryItem
                        })}
                        label="Country"
                        className="tonnage-search-item"
                        value={agentSearchData.query.countries}
                        onValueChange={(value: QueryItem[]) => {
                            dispatch(setCountries(value));
                        }}
                    ></TagsFilter>
                    <PortMultipleSelector
                        onPortChange={(value: PortModel[]) => {
                            if (value) {
                                dispatch(setPortNames(portsToQueryItems(value)));
                            }
                        }}
                        useFreeText={true}
                        portLabel='Port Open'
                        hideArea={true}
                        portValues={queryItemsToPorts(agentSearchData.query.portNames)}
                        className="tonnage-search-item"
                        useAreaBookmark={true}
                    >
                    </PortMultipleSelector>
                    <CargoTypeMultipleSelector
                        onCargoTypeChange={(value: CargoTypeModel[]) => {
                            if (value) {
                                dispatch(setCargoesHandling(cargoTypesToQueryItems(value)));
                            }
                        }}
                        cargoTypeValues={queryItemsToCargoTypes(agentSearchData.query.cargoesHandling)}
                        className="tonnage-search-item"
                    >
                    </CargoTypeMultipleSelector>
                </div>
            </CustomAccordion>
        </>
    )
};

export default AgentSearch;